/**
 * API 檢視模式：打開後，滑鼠移到有 data-inspect 的按鍵、連結、輸入框上，就浮出一張卡片，
 * 寫這個動作打哪支 API、輸入輸出、讀寫哪裡的資料、檢查哪個權限碼（內容在 lib/inspect/registry.ts）。
 *   - 卡片 pointer-events: none，點擊與輸入照常運作，不擋操作
 *   - 手機（觸控）：長按元件看卡片（從底部滑出），點任何地方收起；長按那一下不會觸發點擊
 *   - 開關：頁頭「API 檢視」鈕，或網址 ?inspect=1／?inspect=0；記在這台瀏覽器（localStorage）
 *   - 伺服器沒設 SRV_INSPECT=1 時 available=false：不出現開關、不掛任何事件、不載入註冊表
 */
"use client";

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { InspectCard, type PageSource } from "@/components/inspect/inspect-card";
import type { InspectEntry } from "@/lib/inspect/types";

const STORAGE_KEY = "srv_inspect";
const LONG_PRESS_MS = 450;

interface Ctx { available: boolean; on: boolean; toggle: () => void }
const InspectContext = createContext<Ctx>({ available: false, on: false, toggle: () => {} });
export const useInspect = () => useContext(InspectContext);

/** localStorage 在無痕視窗等情況可能丟錯，一律包起來 */
function readStored(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
}
function writeStored(on: boolean) {
  try { if (on) localStorage.setItem(STORAGE_KEY, "1"); else localStorage.removeItem(STORAGE_KEY); } catch { /* 存不了就只在這頁有效 */ }
}

export function InspectProvider({ available, children }: { available: boolean; children: ReactNode }) {
  const [on, setOn] = useState(false);
  const pathname = usePathname();

  // 開關狀態：網址 ?inspect=1／0 優先（並記住），否則看上次的設定；換頁時重看一次網址
  useEffect(() => {
    if (!available) return;
    const q = new URLSearchParams(window.location.search).get("inspect");
    if (q === "1" || q === "0") writeStored(q === "1");
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 開關存在瀏覽器端，只能掛載後讀
    setOn(q === "1" || (q !== "0" && readStored()));
  }, [available, pathname]);

  const toggle = useCallback(() => {
    setOn((v) => { writeStored(!v); return !v; });
  }, []);

  return (
    <InspectContext.Provider value={{ available, on: available && on, toggle }}>
      {children}
      {available && on && <InspectLayer />}
    </InspectContext.Provider>
  );
}

interface Hit { id: string; el: Element; touch: boolean }

/** 開啟時才掛上的那一層：載入註冊表、監聽滑鼠／長按、畫外框與卡片 */
function InspectLayer() {
  const [registry, setRegistry] = useState<Record<string, InspectEntry> | null>(null);
  const [hit, setHitState] = useState<Hit | null>(null);
  // 外框位置：記是哪個元件量的，換元件時舊的位置不會閃一下
  const [rect, setRect] = useState<{ el: Element; r: DOMRect } | null>(null);
  // 事件處理要讀目前的 hit：狀態與 ref 一起改
  const hitRef = useRef<Hit | null>(null);
  const setHit = useCallback((h: Hit | null) => { hitRef.current = h; setHitState(h); }, []);

  // 註冊表動態載入：關閉時不會打包進一般頁面
  useEffect(() => {
    let alive = true;
    import("@/lib/inspect/registry").then((m) => { if (alive) setRegistry(m.REGISTRY); });
    return () => { alive = false; };
  }, []);

  // <html data-inspecting>：globals.css 用它給每個元件畫虛線外框、關掉手機長按選單
  useEffect(() => {
    document.documentElement.dataset.inspecting = "";
    return () => { delete document.documentElement.dataset.inspecting; };
  }, []);

  useEffect(() => {
    const find = (t: EventTarget | null) => (t instanceof Element ? t.closest("[data-inspect]") : null);

    // ── 滑鼠：移到哪個元件就顯示哪張 ──
    const onOver = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      const el = find(e.target);
      if (!el) { if (hitRef.current && !hitRef.current.touch) setHit(null); return; }
      if (hitRef.current?.el !== el) setHit({ id: el.getAttribute("data-inspect") ?? "", el, touch: false });
    };
    const onLeaveWindow = (e: PointerEvent) => { if (!e.relatedTarget && !hitRef.current?.touch) setHit(null); };

    // ── 觸控：長按顯示，下一次點任何地方收起 ──
    let timer: ReturnType<typeof setTimeout> | undefined;
    let start: { x: number; y: number } | null = null;
    let swallowClick = false;
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      if (hitRef.current?.touch) { setHit(null); return; }
      const el = find(e.target);
      if (!el) return;
      start = { x: e.clientX, y: e.clientY };
      timer = setTimeout(() => {
        swallowClick = true;
        setHit({ id: el.getAttribute("data-inspect") ?? "", el, touch: true });
      }, LONG_PRESS_MS);
    };
    const cancel = () => { clearTimeout(timer); start = null; };
    const onMove = (e: PointerEvent) => {
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) cancel();
    };
    // 長按放開後瀏覽器還是會送 click：吃掉這一次，避免長按看卡片時誤觸按鍵
    const onClick = (e: MouseEvent) => {
      if (!swallowClick) return;
      swallowClick = false;
      e.preventDefault();
      e.stopPropagation();
    };
    const onContextMenu = (e: MouseEvent) => { if (swallowClick || (start && find(e.target))) e.preventDefault(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setHit(null); };

    document.addEventListener("pointerover", onOver, true);
    document.addEventListener("pointerout", onLeaveWindow, true);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointermove", onMove, true);
    document.addEventListener("pointerup", cancel, true);
    document.addEventListener("pointercancel", cancel, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("contextmenu", onContextMenu, true);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerover", onOver, true);
      document.removeEventListener("pointerout", onLeaveWindow, true);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("pointermove", onMove, true);
      document.removeEventListener("pointerup", cancel, true);
      document.removeEventListener("pointercancel", cancel, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("contextmenu", onContextMenu, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [setHit]);

  // 外框位置跟著捲動、縮放、元件被移除（例如換頁、彈窗關掉）更新
  useLayoutEffect(() => {
    if (!hit) return;
    let raf = 0;
    const update = () => {
      if (!hit.el.isConnected) { setHit(null); return; }
      setRect({ el: hit.el, r: hit.el.getBoundingClientRect() });
    };
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const timer = setInterval(update, 500);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(timer);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [hit, setHit]);

  if (!hit || rect?.el !== hit.el) return null;
  const r = rect.r;
  const entry = registry?.[hit.id];
  // 「源頭 GET」：不另發請求的元件，資料來自這頁載入時那支 GET（頁面大標題掛的 xxx.page）
  let source: PageSource | null = null;
  if (entry?.call.kind === "client") {
    const pageId = document.querySelector("[data-inspect$='.page']")?.getAttribute("data-inspect");
    const page = pageId ? registry?.[pageId] : undefined;
    source = { http: `GET ${window.location.pathname}`, fn: page?.call.fn, file: page?.call.file };
  }
  // 連結：多寫這一個實際會打的網址（列表每一列不同）
  const href = hit.el instanceof HTMLAnchorElement ? hit.el.getAttribute("href") : null;

  return createPortal(
    <>
      <div
        aria-hidden
        className="pointer-events-none fixed z-[200] rounded-md border-2 border-primary/80 bg-primary/5"
        style={{ left: r.left - 3, top: r.top - 3, width: r.width + 6, height: r.height + 6 }}
      />
      <InspectCard id={hit.id} entry={entry} loading={!registry} href={href} anchor={r} sheet={hit.touch} source={source} onClose={() => setHit(null)} />
    </>,
    document.body,
  );
}
